const AWS_WAF_TOKEN_HEADER = "x-aws-waf-token";
const AWS_WAF_TIMEOUT = 10000;
const COGNITO_URL_REGEX = /^https:\/\/cognito-idp[.-][a-z0-9.-]+\.amazonaws\.com(\.cn)?\//;
const AMZ_TARGET_HEADER = "x-amz-target";
const CONFIRM_FORGOT_PASSWORD_TARGET = "AWSCognitoIdentityProviderService.ConfirmForgotPassword";

let isFetchWrapped = false;
let sdkLoadPromise = null;

const isAwsWafEnabled = () => !!process.env.REACT_APP_WAF_URL;

const withTimeout = promise =>
  new Promise(resolve => {
    const timer = setTimeout(() => resolve(null), AWS_WAF_TIMEOUT);
    Promise.resolve(promise).then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      }
    );
  });

export const loadAwsWafSdk = () => {
  if (isAwsWafEnabled() === false) {
    return Promise.resolve(false);
  }
  if (!sdkLoadPromise) {
    sdkLoadPromise = new Promise(resolve => {
      const sdkUrl = process.env.REACT_APP_WAF_URL;
      const script = document.createElement("script");
      script.src = sdkUrl.endsWith(".js") ? sdkUrl : `${sdkUrl.replace(/\/?$/, "/")}jsapi.js`;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => {
        sdkLoadPromise = null;
        script.remove();
        resolve(false);
      };
      document.head.appendChild(script);
    });
  }
  return sdkLoadPromise;
};

const getAwsWafToken = () => {
  if (isAwsWafEnabled() === false || !sdkLoadPromise) {
    return Promise.resolve(null);
  }
  return withTimeout(
    sdkLoadPromise.then(isSdkLoaded => {
      if (isSdkLoaded !== true || !window.AwsWafIntegration) {
        return null;
      }
      return window.AwsWafIntegration.getToken();
    })
  );
};

const getAmzTarget = headers => {
  if (!headers) {
    return null;
  }
  if (typeof headers.get === "function") {
    return headers.get(AMZ_TARGET_HEADER);
  }
  return headers[AMZ_TARGET_HEADER] || headers["X-Amz-Target"] || null;
};

export const installAwsWafTokenForCognito = () => {
  if (isAwsWafEnabled() === false || isFetchWrapped === true) {
    return;
  }
  isFetchWrapped = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (resource, options) => {
    const init = options || {};
    // Amplify v6 passes a URL instance, which has .href, not .url (a Request has .url)
    const url = typeof resource === "string" ? resource : resource?.url ?? String(resource);
    const isConfirmForgotPassword =
      getAmzTarget(init.headers) === CONFIRM_FORGOT_PASSWORD_TARGET && !!url && COGNITO_URL_REGEX.test(url);
    if (isConfirmForgotPassword === false) {
      return originalFetch(resource, options);
    }

    const token = await getAwsWafToken();
    if (!token) {
      return originalFetch(resource, options);
    }

    const headers = new Headers(init.headers || (resource instanceof Request ? resource.headers : {}));
    headers.set(AWS_WAF_TOKEN_HEADER, token);
    return originalFetch(resource, { ...init, headers });
  };
};
